import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Loader2, Mail, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }); } catch (_) { return iso; }
};

export default function BoardPackAutoDispatch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/board-pack/auto-dispatch/status");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (val) => {
    setBusy(true);
    try {
      await apiClient.put("/board-pack/auto-dispatch/toggle", { enabled: val });
      setData(d => ({ ...d, enabled: val }));
      toast.success(val ? "Monthly auto-dispatch enabled." : "Monthly auto-dispatch disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    } finally { setBusy(false); }
  };

  const dispatchNow = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post("/board-pack/auto-dispatch/check");
      if (res.data.dispatched) {
        toast.success(`Board pack dispatched to ${res.data.recipients?.length || 0} recipient(s).`);
        load();
      } else {
        const reason = res.data.reason || "no_dispatch";
        const msg = {
          not_dispatch_window: "Auto-dispatch only runs on the 1st–3rd of the month.",
          already_sent_this_month: `Already sent this month (${res.data.monthKey}).`,
          auto_send_disabled: "Auto-dispatch is currently disabled.",
          no_email_provider: "Resend email provider not configured.",
          no_recipients: "No org owners to send to.",
        }[reason] || `Not dispatched: ${reason}`;
        toast.info(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch check failed");
    } finally { setBusy(false); }
  };

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="board-pack-auto-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading auto-dispatch status…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="board-pack-auto-dispatch">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Mail className="h-4 w-4 text-indigo-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 text-sm">Monthly Board Pack · Auto-Dispatch</h3>
            {data.enabled && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 uppercase tracking-wider"><CheckCircle2 className="h-3 w-3"/>Enabled</span>}
          </div>
          <p className="text-[11px] text-slate-500">{data.nextDispatchWindow} · last sent {fmtDate(data.lastSentAt)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!!data.enabled} onCheckedChange={toggle} disabled={busy} data-testid="board-pack-auto-toggle"/>
          <Button variant="outline" size="sm" onClick={dispatchNow} disabled={busy} className="h-8" data-testid="board-pack-auto-dispatch-now">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5 mr-1"/>}
            Dispatch check
          </Button>
        </div>
      </div>
    </Card>
  );
}
